import { useTranslation } from "react-i18next";
import { IGetReviewsResponse } from "../../../api/types";
import { IGymListing } from "../../../models/GymListing";
import { ISettings } from "../../../models/Settings";
import { IUserSubscriptionModel } from "../../../models/UserSubscription";
import starSvg from "../../../assets/images/icons/star.svg";
import starOutlineSvg from "../../../assets/images/icons/star-outline.svg";
import arrowLeftDark from "../../../assets/images/icons/arrow-left-dark.svg";
import arrowRightDark from "../../../assets/images/icons/arrow-right-dark2.svg";
import Loader from "../../../components/shared/Loader/Loader";
import { NoReviewsYet } from "./NoReviewsYet";
import nextArrow from "../../../assets/images/icons/slider-next-arrow.svg";
import prevArrow from "../../../assets/images/icons/slider-previous-arrow.svg";

const NextArrow = (props: any) => {
  const { className, onClick } = props;
  return <img src={nextArrow} alt="next" className={className} onClick={onClick} />;
};
const PrevArrow = (props: any) => {
  const { className, onClick } = props;
  return <img src={prevArrow} alt="prev" className={className} onClick={onClick} />;
};

export const formatToMonthDayYear = (iso?: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

export const InfoTab = ({
  gym,
  userSubscription,
  settings,
  isLoadingReviews,
  isFetching,
  reviews,
  currentPage,
  totalPages,

  handlePrevPage,
  handleNextPage,
}: {
  gym: IGymListing;
  userSubscription: IUserSubscriptionModel | null;
  settings: ISettings | null;
  isLoadingReviews: boolean;
  isFetching: boolean;
  reviews: IGetReviewsResponse | null;
  currentPage: number;
  totalPages: number;

  handlePrevPage: () => void;
  handleNextPage: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* === RATING SECTION === */}
      <div className="rating-wrapper">
        <h3 className="title">{gym.businessName}</h3>
        <div className="rating">
          <p>{gym.reviews?.averageRating || "0.0"}</p>
          <div className="stars-wrapper">
            {Array.from({ length: 5 }).map((_, i) => (
              <img
                key={i}
                src={
                  i + 1 <= Math.floor(gym.reviews?.averageRating ?? 0) ? starSvg : starOutlineSvg
                }
                alt="star"
              />
            ))}
          </div>
          <span>({gym.reviews?.totalCount || "0"})</span>
        </div>
        <p className="description">{gym.description}</p>
      </div>

      {/* === CANCELLATION POLICY === */}
      {userSubscription && (
        <div className="cancelation-wrapper">
          <h3 className="title">{t("CancelationPolicy")}</h3>
          <p className="description">
            {t("Cancel")} <strong>{settings?.refundCancellationHours}</strong>{" "}
            {t("CancellationPolicyDescription")}
          </p>
        </div>
      )}

      {gym.comingSoon ? null : (
        <div className="review-wrapper">
          <h3 className="title">{t("GymReviews")}</h3>
          <div className="gym-reviews">
            <div className="rating-box">
              <span className="rating-score">{gym.reviews?.averageRating || "0.0"}</span>
              <div className="stars-wrapper">
                {Array.from({ length: 5 }).map((_, i) => {
                  const rating = gym.reviews?.averageRating ?? 0;
                  const filled = i + 1 <= Math.floor(rating);
                  return <img key={i} src={filled ? starSvg : starOutlineSvg} alt="star" />;
                })}
              </div>
              <p className="rating-count">
                {gym.reviews?.totalCount || "0"} {t("Ratings").toLowerCase()}
              </p>
            </div>

            <div className="rating-bars">
              {Array.from({ length: 5 }, (_, i) => {
                const rating = 5 - i; // 5 → 1
                const count =
                  gym.reviews?.reviewsByRatingNumber?.[rating as 1 | 2 | 3 | 4 | 5] ?? 0;
                const total = gym.reviews?.totalCount || 1;
                const percent = (count / total) * 100;

                return (
                  <div className="bar" key={rating}>
                    <span>{rating}</span>
                    <div className="progress">
                      <div className="progress-fill" style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {isLoadingReviews || isFetching ? (
            <Loader />
          ) : (
            <>
              {reviews?.data?.map((review, i) => (
                <div className="review-card" key={i}>
                  <div className="review-header">
                    <div className="stars-wrapper">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <img
                          key={idx}
                          src={idx + 1 <= Math.floor(review.rating ?? 0) ? starSvg : starOutlineSvg}
                          alt="star"
                        />
                      ))}
                    </div>
                    <span className="date">{formatToMonthDayYear(review.createdAt)}</span>
                  </div>
                  <p className="description">{review.comment}</p>
                </div>
              ))}

              {reviews && reviews?.data?.length > 0 ? (
                <div className="pagination">
                  <button
                    className="page-button"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    <img src={arrowLeftDark} alt="prev" />
                  </button>
                  <span>
                    {t("Page")} {currentPage} {t("of")} {totalPages}
                  </span>
                  <button
                    className="page-button"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    <img src={arrowRightDark} alt="next" />
                  </button>
                </div>
              ) : (
                <NoReviewsYet />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};
